import axios from 'axios';
import { resolveAdminAccessToken } from '../auth/adminAccessToken';

export type ContentTypeRef = { applicationKey: string; contentTypeKey: string };
export type ContentDraftStatus = 'DRAFT' | 'IN_REVIEW' | 'PUBLISHED';
export type ContentPublicationStatus = 'DRAFT' | 'PUBLISHED' | 'RETIRED';

export type ContentValidationIssue = {
  code: string;
  path: string;
  message: string;
  severity: 'ERROR' | 'WARNING';
};

export type ContentValidationReport = {
  schemaVersion: number;
  issues: ContentValidationIssue[];
  errorCount: number;
  warningCount: number;
  publishable: boolean;
};

export type ContentItemSummary = {
  key: { type: ContentTypeRef; slug: string };
  title: string;
  publicationStatus: ContentPublicationStatus;
  draftStatus: ContentDraftStatus | null;
  currentVersionNumber: number | null;
  validation: ContentValidationReport;
  updatedAt: string;
  attributes: Record<string, string | null>;
};

export type ContentVersionSummary = {
  versionNumber: number;
  sourceContentHash: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
};

export type ContentDraftView<TDocument> = {
  key: { type: ContentTypeRef; slug: string };
  draftId: string;
  revision: number;
  status: ContentDraftStatus;
  document: TDocument;
  validation: ContentValidationReport;
  versions: ContentVersionSummary[];
  createdBy: string;
  updatedBy: string;
  reviewedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ContentAuditEntry = {
  auditId: string;
  action: string;
  actorId: string;
  revision: number | null;
  versionNumber: number | null;
  details: Record<string, unknown>;
  occurredAt: string;
};

export type ContentHistoryView = {
  key: { type: ContentTypeRef; slug: string };
  currentVersionNumber: number | null;
  versions: ContentVersionSummary[];
  auditEntries: ContentAuditEntry[];
};

const contentClient = axios.create({ baseURL: '/' });
contentClient.interceptors.request.use((config) => {
  const token = resolveAdminAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const typePath = ({ applicationKey, contentTypeKey }: ContentTypeRef): string =>
  `/api/admin/content/${encodeURIComponent(applicationKey)}/${encodeURIComponent(contentTypeKey)}`;

const itemPath = (type: ContentTypeRef, slug: string): string =>
  `${typePath(type)}/items/${encodeURIComponent(slug)}`;

const requireRecord = (value: unknown, path: string): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`CMS 응답의 ${path} 구조가 올바르지 않습니다.`);
  }
  return value as Record<string, unknown>;
};

const requireDraft = <TDocument>(value: unknown): ContentDraftView<TDocument> => {
  const root = requireRecord(value, 'draft');
  if (typeof root.revision !== 'number' || typeof root.draftId !== 'string') {
    throw new Error('CMS 초안 식별자 또는 revision이 없습니다.');
  }
  requireRecord(root.document, 'document');
  requireRecord(root.validation, 'validation');
  return value as ContentDraftView<TDocument>;
};

export async function listContentItems(
  type: ContentTypeRef,
  filter: Record<string, string | undefined>,
): Promise<ContentItemSummary[]> {
  const response = await contentClient.get<unknown>(`${typePath(type)}/items`, { params: filter });
  if (!Array.isArray(response.data)) throw new Error('CMS 목록 응답이 배열이 아닙니다.');
  return response.data.map((value, index) => {
    const item = requireRecord(value, `items[${index}]`);
    requireRecord(item.key, `items[${index}].key`);
    requireRecord(item.validation, `items[${index}].validation`);
    return value as ContentItemSummary;
  });
}

export async function openContentDraft<TDocument>(
  type: ContentTypeRef,
  slug: string,
): Promise<ContentDraftView<TDocument>> {
  const response = await contentClient.post<unknown>(`${itemPath(type, slug)}/draft/open`);
  return requireDraft<TDocument>(response.data);
}

export async function getContentDraft<TDocument>(
  type: ContentTypeRef,
  slug: string,
): Promise<ContentDraftView<TDocument>> {
  const response = await contentClient.get<unknown>(`${itemPath(type, slug)}/draft`);
  return requireDraft<TDocument>(response.data);
}

export async function saveContentDraft<TDocument>(
  type: ContentTypeRef,
  slug: string,
  expectedRevision: number | null,
  document: TDocument,
): Promise<ContentDraftView<TDocument>> {
  const response = await contentClient.put<unknown>(`${itemPath(type, slug)}/draft`, {
    expectedRevision,
    document,
  });
  return requireDraft<TDocument>(response.data);
}

export async function validateContent<TDocument>(
  type: ContentTypeRef,
  document: TDocument,
): Promise<ContentValidationReport> {
  const response = await contentClient.post<ContentValidationReport>(`${typePath(type)}/validate`, document);
  return response.data;
}

export async function submitContentReview<TDocument>(
  type: ContentTypeRef,
  slug: string,
  expectedRevision: number,
): Promise<ContentDraftView<TDocument>> {
  const response = await contentClient.post<unknown>(`${itemPath(type, slug)}/review`, { expectedRevision });
  return requireDraft<TDocument>(response.data);
}

export async function publishContent<TDocument>(
  type: ContentTypeRef,
  slug: string,
  expectedRevision: number,
  confirmationKey: string,
): Promise<ContentDraftView<TDocument>> {
  const response = await contentClient.post<unknown>(`${itemPath(type, slug)}/publish`, {
    expectedRevision,
    confirmationKey,
  });
  return requireDraft<TDocument>(response.data);
}

export async function retireContent(
  type: ContentTypeRef,
  slug: string,
  confirmationKey: string,
): Promise<ContentItemSummary> {
  const response = await contentClient.post<ContentItemSummary>(`${itemPath(type, slug)}/retire`, {
    confirmationKey,
  });
  return response.data;
}

export async function getContentHistory(type: ContentTypeRef, slug: string): Promise<ContentHistoryView> {
  const response = await contentClient.get<ContentHistoryView>(`${itemPath(type, slug)}/history`);
  return response.data;
}

export async function restoreContentVersion<TDocument>(
  type: ContentTypeRef,
  slug: string,
  expectedRevision: number | null,
  versionNumber: number,
  confirmationKey: string,
): Promise<ContentDraftView<TDocument>> {
  const response = await contentClient.post<unknown>(`${itemPath(type, slug)}/restore`, {
    expectedRevision,
    versionNumber,
    confirmationKey,
  });
  return requireDraft<TDocument>(response.data);
}
